import React, { useState, forwardRef, useImperativeHandle, useContext } from "react";
import { Modal, Button, Divider, Select, Input, message } from "antd";
import PropTypes from "prop-types";
import { ClassificationsContext } from "../../../../contexts/ClassificationsContext";
import { extractDepthClassifications } from "../../utils";
import { edit_company } from "../../../../services";
import { CompaniesContext } from "../../../../contexts";

export const ClassificationModal = forwardRef(({ token }, ref) => {
	const { selectedCompany: { id: companyId }} = useContext(CompaniesContext);
	const { classifications, setClassifications } = useContext(ClassificationsContext);

	const [openModal, setOpenModal] = useState(false);
	const [loading, setLoading] = useState(false);

	const [parentId, setParentId] = useState("");
	const [classificationName, setClassificationName] = useState("");
		
	useImperativeHandle(ref, () => ({		
		openModal: () => setOpenModal(true),
		closeModal: () => setOpenModal(false),
	}));
	
	const handleClose = () => {
		if(loading) return;
		
		setOpenModal(false);
	};

	const handleSave = async () => {
		if(parentId.trim() === "") {			
			message.warning("Porfavor Selecciona una sub-categoría");
			return;
		}

		if(classificationName.trim() === "") {			
			message.warning("Porfavor escribe el nombre de la nueva clasificación");
			return;
		}

		const findNodeById = (nodes, id) => {
			for (const node of nodes) {
				if (node.id === id) return node;
				if (node.children && node.children.length > 0) {
					const found = findNodeById(node.children, id);
					if (found) return found;
				}
			}
			return null;
		};
		
		const parent = findNodeById(classifications, parentId);
		
		if (!parent) {
			message.error("No se encontró la subcategoría seleccionada.");
			return;
		}
				
		const nameExistsInChildren = parent.children?.some(
			child => child.classification_name.trim().toLowerCase() === classificationName.trim().toLowerCase()
		);
		
		if (nameExistsInChildren) {
			message.warning("Ya existe una sub-clasificación con ese nombre bajo la categoría seleccionada.");
			return;
		}

		setLoading(true);
				
		try {							
			const res = await edit_company(token, {
				companyId,
				name: classificationName,
				parentId: parentId,
			}, "add_classifications");

			if(!res){
				message.error("Error al guardar y agregar la nueva clasificación");
				setLoading(false);
				return;
			}

			const { newIdClassification } = res;
			
			const newChild = {
				id: newIdClassification,
				classification_name: classificationName.trim(),
				classification: `${parent.classification}-${classificationName.trim()}`,
				sort_order: parent.children.length + 1,
				depth: parent.depth + 1,
				parent_id: parent.id,
				children_ids: [],
				children: [],
			};
			
			const addChildToParent = (nodes) => {
				return nodes.map((node) => {
					if (node.id === parent.id) {
						return {
							...node,
							children_ids: [...node.children_ids, newChild.id],
							children: [...node.children, newChild],
						};
					}
					if (node.children && node.children.length > 0) {
						return {
							...node,
							children: addChildToParent(node.children),
						};
					}
					return node;
				});
			};
			
			const updatedTree = addChildToParent(classifications);
			setClassifications(updatedTree);

			setOpenModal(false);
			setClassificationName("");
			setParentId("");

			message.success("Clasificación agregada exitosamente");
		} catch (err) {
			message.error(err.response.data);
		} finally {
			setLoading(false);
		}
	};

	const modalContent = (
		<>
			<Divider orientation="left">Sub Categoría a Seleccionar</Divider>
			<Select
				value={parentId.trim() === "" ? null : parentId}
				showSearch
				allowClear
				placeholder="Selecciona una sub-categoría"				
				style={{ width: "100%" }}
				onChange={(value) => setParentId(value)}
				options={extractDepthClassifications(classifications, 1)}
				optionFilterProp="label"
			/>

			<Divider orientation="left">Nombre de nueva clasificación</Divider>
			<Input
				value={classificationName}
				maxLength={20}
				showCount
				placeholder="Nombre de nueva clasificación"
				onChange={(e) => {
					const filteredValue = e.target.value
						.replace(/-/g, "_");
						
					setClassificationName(filteredValue);
				}}
				type="text"
			/>
		</>
	);
	
	return (
		<Modal
			title="Agregar Nueva Clasificación"
			open={openModal}
			onCancel={handleClose}
			footer={[
				<Button key="cancel" loading={loading} onClick={handleClose}>
					Cancelar
				</Button>,
				<Button key="save" type="primary" loading={loading} onClick={handleSave}>
					Agregar y Guardar
				</Button>,
			]}
		>
			{modalContent}
		</Modal>
	);
});

ClassificationModal.displayName = "ClassificationModal";

ClassificationModal.propTypes = {
	token: PropTypes.string.isRequired,		
};
